/**
 * Hook that runs CSG geometry computation in a Web Worker.
 * Returns BufferGeometry objects rebuilt from transferred arrays.
 * Old geometry stays visible while new one computes (no flash).
 * Debounces config changes and cancels stale workers so only the
 * latest request is ever applied.
 */
import {useState, useEffect, useRef, useCallback} from 'react'
import * as THREE from 'three'
import type {WorkerInput, WorkerOutput} from './deviceGeometryWorker'
import {reportError} from '../../errors/reportError'
import {isRenderApp} from './renderAppMode'
import {computeGeometryHash, fetchGeometryCache, storeGeometryCache} from './geometryCache'

// Watchdog for the CSG worker — geometry normally builds in well under a
// second, so if no result arrives within the window the worker has hung or
// crashed. We then stop "loading" so the overlay can clear instead of hanging.
// Generous headroom: a feature-dense device (e.g. a many-grill iPad) doing heavy
// CSG on a slow/contended server CPU can take many seconds, and we must not kill
// a correct-but-slow build (the deviceless-thumbnail prod symptom). Full-detail
// renders (full-size, credit-charged) build ~4× the body polys of a thumbnail,
// so they get a much longer window; thumbnails finish fast regardless.
const WORKER_TIMEOUT_MS = 90000
// Full-size renders are now on-demand background jobs (one at a time), so a
// generous watchdog is safe — a feature-dense device on the slow prod CPU can
// take many minutes. Kept below the BE Puppeteer render wait (18 min).
const FULL_RENDER_WORKER_TIMEOUT_MS = 900000

// Automatic re-dispatches of a failed/hung worker before giving up. A transient
// failure (e.g. a flaky module-worker load on a cold/headless server) usually
// succeeds on the immediate retry.
const MAX_WORKER_RETRIES = 1

// Puppeteer readiness flags live on `window`. Only the render micro-frontend
// (RenderApp.tsx) reads them — the editor's loading overlay is driven by the
// React `workerRunning` state, not these. Splitting success (`__SCENE_READY`)
// from failure (`__SCENE_GEOMETRY_FAILED`) lets the thumbnail/render scheduler
// tell "geometry is ready" apart from "geometry gave up", so it never
// screenshots a scene whose device mesh never arrived (the prod symptom where
// background + texts + sprites + decals render but the worker geometry is gone).
interface SceneSignals {
  __SCENE_READY?: boolean
  __SCENE_GEOMETRY_FAILED?: boolean
  /** Reason of the last geometry failure (worker throw / onerror / timeout) — surfaced in puppeteer diagnostics. */
  __SCENE_GEOMETRY_ERROR?: string
  /** How many worker dispatches happened — a large count means a re-dispatch storm, not a single hang. */
  __SCENE_GEOMETRY_ATTEMPTS?: number
  /** Last build stage the worker reported reaching — on a hang this is where it stalled. */
  __SCENE_GEOMETRY_STAGE?: string
}
const signals = () => window as unknown as SceneSignals
const setSceneReady = (ready: boolean) => {
  signals().__SCENE_READY = ready
}
const setGeometryFailed = (failed: boolean, reason?: string) => {
  const s = signals()
  s.__SCENE_GEOMETRY_FAILED = failed
  if (failed && reason) s.__SCENE_GEOMETRY_ERROR = reason
}

// ── Deserialization ───────────────────────────────────────────────

interface SerializedGeo {
  position: Float32Array
  normal: Float32Array
  uv: Float32Array | null
  index: Uint32Array | null
}

const deserializeGeo = (s: SerializedGeo): THREE.BufferGeometry => {
  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.BufferAttribute(s.position, 3))
  geo.setAttribute('normal', new THREE.BufferAttribute(s.normal, 3))
  if (s.uv) geo.setAttribute('uv', new THREE.BufferAttribute(s.uv, 2))
  if (s.index) geo.setIndex(new THREE.BufferAttribute(s.index, 1))
  return geo
}

// ── Result type ───────────────────────────────────────────────────

export interface DeviceGeometries {
  bodyGeo: THREE.BufferGeometry
  frontFaceGeo: THREE.BufferGeometry
  displayGeo: THREE.BufferGeometry
  antennaGeos: THREE.BufferGeometry[]
  sideBtnGeos: THREE.BufferGeometry[]
  sideBtnSlotGeos: THREE.BufferGeometry[]
  portShellGeos: THREE.BufferGeometry[]
  portTongueGeos: THREE.BufferGeometry[]
  portInsertGeos: THREE.BufferGeometry[]
  frontSpeakerInsertGeos: THREE.BufferGeometry[]
  frontSpeakerDiscGeos: THREE.BufferGeometry[]
  homeButtonPlugGeos: THREE.BufferGeometry[]
  speakerGrillDiscGeos: THREE.BufferGeometry[]
  speakerGrillSlotGeos: THREE.BufferGeometry[]
  screwDiscGeos: THREE.BufferGeometry[]
  simCardSlotTrayGeos: THREE.BufferGeometry[]
  simCardSlotSlotGeos: THREE.BufferGeometry[]
  muteSwitchInsertGeos: THREE.BufferGeometry[]
  muteSwitchSlotGeos: THREE.BufferGeometry[]
}

// ── Dispose helper ────────────────────────────────────────────────

const disposeGeometries = (geos: DeviceGeometries) => {
  geos.bodyGeo.dispose()
  geos.frontFaceGeo.dispose()
  geos.displayGeo.dispose()
  geos.antennaGeos.forEach((g) => g.dispose())
  geos.sideBtnGeos.forEach((g) => g.dispose())
  geos.sideBtnSlotGeos.forEach((g) => g.dispose())
  geos.portShellGeos.forEach((g) => g.dispose())
  geos.portTongueGeos.forEach((g) => g.dispose())
  geos.portInsertGeos.forEach((g) => g.dispose())
  geos.frontSpeakerInsertGeos.forEach((g) => g.dispose())
  geos.frontSpeakerDiscGeos.forEach((g) => g.dispose())
  geos.homeButtonPlugGeos.forEach((g) => g.dispose())
  geos.speakerGrillDiscGeos.forEach((g) => g.dispose())
  geos.speakerGrillSlotGeos.forEach((g) => g.dispose())
  geos.screwDiscGeos.forEach((g) => g.dispose())
  geos.simCardSlotTrayGeos.forEach((g) => g.dispose())
  geos.simCardSlotSlotGeos.forEach((g) => g.dispose())
  geos.muteSwitchInsertGeos.forEach((g) => g.dispose())
  geos.muteSwitchSlotGeos.forEach((g) => g.dispose())
}

// ── Hook ──────────────────────────────────────────────────────────

interface UseDeviceGeometryResult {
  geometries: DeviceGeometries | null
  /** True only when no geometry has been produced yet (first load / new scene) */
  initialLoading: boolean
  /** True when geometry already exists and the worker is computing an update */
  updating: boolean
}

export const useDeviceGeometry = (config: WorkerInput): UseDeviceGeometryResult => {
  const [result, setResult] = useState<DeviceGeometries | null>(null)
  const [workerRunning, setWorkerRunning] = useState(true)
  const prevGeosRef = useRef<DeviceGeometries | null>(null)
  const requestIdRef = useRef(0)
  const activeWorkerRef = useRef<Worker | null>(null)
  const watchdogRef = useRef<number | null>(null)

  const clearWatchdog = () => {
    if (watchdogRef.current !== null) {
      window.clearTimeout(watchdogRef.current)
      watchdogRef.current = null
    }
  }

  // Deliver a WorkerOutput (from the worker OR the geometry cache): swap in the
  // new geometries, clear loading, and signal readiness for Puppeteer. Shared by
  // both paths so a cache hit is indistinguishable from a fresh build.
  const applyGeometry = useCallback((data: WorkerOutput) => {
    if (prevGeosRef.current) {
      disposeGeometries(prevGeosRef.current)
    }
    const geos: DeviceGeometries = {
      bodyGeo: deserializeGeo(data.body),
      frontFaceGeo: deserializeGeo(data.frontFace),
      displayGeo: deserializeGeo(data.display),
      antennaGeos: data.antennas.map(deserializeGeo),
      sideBtnGeos: data.sideButtons.map(deserializeGeo),
      sideBtnSlotGeos: data.sideButtonSlots.map(deserializeGeo),
      portShellGeos: data.portShells.map(deserializeGeo),
      portTongueGeos: data.portTongues.map(deserializeGeo),
      portInsertGeos: data.portInserts.map(deserializeGeo),
      frontSpeakerInsertGeos: data.frontSpeakerInserts.map(deserializeGeo),
      frontSpeakerDiscGeos: data.frontSpeakerDiscs.map(deserializeGeo),
      homeButtonPlugGeos: data.homeButtonPlugs.map(deserializeGeo),
      speakerGrillDiscGeos: data.speakerGrillDiscs.map(deserializeGeo),
      speakerGrillSlotGeos: data.speakerGrillSlots.map(deserializeGeo),
      screwDiscGeos: data.screwDiscs.map(deserializeGeo),
      simCardSlotTrayGeos: data.simCardSlotTrays.map(deserializeGeo),
      simCardSlotSlotGeos: data.simCardSlotSlots.map(deserializeGeo),
      muteSwitchInsertGeos: data.muteSwitchInserts.map(deserializeGeo),
      muteSwitchSlotGeos: data.muteSwitchSlots.map(deserializeGeo),
    }
    prevGeosRef.current = geos
    setResult(geos)
    setWorkerRunning(false)
    setGeometryFailed(false)
    setSceneReady(true)
  }, [])

  const postToWorker = useCallback(
    (cfg: WorkerInput, cacheHash?: string) => {
      // Spin up one worker; `attempt` lets a transient failure re-dispatch in place
      // (recursive arrow — `launch` is only invoked async, so the self-reference is
      // resolved by call time).
      const launch = (attempt: number) => {
        // Increment request ID — any response with an older ID will be discarded
        const id = ++requestIdRef.current
        setWorkerRunning(true)

        // Reset readiness flags — Puppeteer must wait for this computation to
        // finish, and a fresh (re)dispatch clears any prior failure.
        setSceneReady(false)
        setGeometryFailed(false)
        // Count dispatches across the page's lifetime so diagnostics can tell a
        // single hang apart from a re-dispatch storm (config thrashing).
        const s = signals()
        s.__SCENE_GEOMETRY_ATTEMPTS = (s.__SCENE_GEOMETRY_ATTEMPTS ?? 0) + 1

        // Terminate previous worker to cancel stale computation
        if (activeWorkerRef.current) {
          activeWorkerRef.current.terminate()
        }
        clearWatchdog()

        // Spin up a fresh worker for this request.
        // The `new URL(...)` must be inlined directly inside `new Worker(...)` so
        // Vite can statically detect and bundle the worker; wrapping it in a
        // helper leaves a raw .ts URL in the production build (worker fails to load).
        const w = new Worker(new URL('./deviceGeometryWorker.ts', import.meta.url), {type: 'module'})
        activeWorkerRef.current = w

        // Recover from a failed worker. Only an instantiation/load `onerror` is
        // retried (it can be transient); a hang (`timed out`) or an in-worker throw
        // is deterministic, so we don't waste another window on it. On final
        // failure: clear the editor overlay (React state) and flag geometry as
        // FAILED for Puppeteer — but do NOT raise __SCENE_READY, so the render
        // scheduler treats a missing device as an error instead of silently
        // screenshotting a deviceless scene.
        const handleWorkerFailure = (reason: string, retryable: boolean) => {
          if (id !== requestIdRef.current) return
          clearWatchdog()
          activeWorkerRef.current?.terminate()
          activeWorkerRef.current = null
          if (retryable && attempt < MAX_WORKER_RETRIES) {
            launch(attempt + 1)
            return
          }
          setWorkerRunning(false)
          const detail = `${reason} (after ${attempt + 1} attempt(s))`
          setGeometryFailed(true, detail)
          reportError(new Error(`Device geometry worker ${detail}`), 'deviceGeometry.workerError')
        }

        const watchdogMs = cfg.lowPoly ? WORKER_TIMEOUT_MS : FULL_RENDER_WORKER_TIMEOUT_MS
        watchdogRef.current = window.setTimeout(() => handleWorkerFailure('timed out', false), watchdogMs)

        w.onerror = (event) => {
          const msg = typeof event === 'string' ? event : (event as ErrorEvent).message || 'unknown'
          handleWorkerFailure(`errored: ${msg}`, true)
        }

        w.onmessage = (e: MessageEvent<WorkerOutput | {error: string} | {progress: string}>) => {
          // Discard if a newer request has been issued since
          if (id !== requestIdRef.current) return

          // Build-stage beacon — record where the worker is (the last one before a
          // hang pinpoints the stalling stage). Does not clear the watchdog.
          if ('progress' in e.data) {
            signals().__SCENE_GEOMETRY_STAGE = e.data.progress
            return
          }

          // The worker caught an exception while building and reported it.
          if ('error' in e.data) {
            handleWorkerFailure(`threw: ${e.data.error}`, false)
            return
          }

          clearWatchdog()
          const data = e.data
          activeWorkerRef.current = null

          applyGeometry(data)

          // Populate the geometry cache (render app only) — fire-and-forget, never
          // blocks readiness. Only reached on a cache MISS (a hit skips the worker).
          if (cacheHash) storeGeometryCache(cacheHash, data)
        }

        w.postMessage(cfg)
      }

      launch(0)
    },
    [applyGeometry],
  )

  // Dispatch worker whenever config changes.
  // Config only changes on commit (slider release) or undo/redo — never during
  // drag, since drag writes to the preview store instead of Redux.
  //
  // Render app: try the persistent geometry cache first (a device is built once,
  // reused across every thumbnail/render/retry). On a hit, deliver directly and
  // skip the worker; on a miss, build and store the result. The editor never
  // touches the cache (fast user CPU; anonymous users must not hit internal
  // endpoints). Staleness is guarded by requestIdRef (bumped up-front, before any
  // await) plus a per-run cancelled flag for unmount.
  useEffect(() => {
    if (!isRenderApp()) {
      postToWorker(config)
      return
    }

    let cancelled = false
    const buildViaCache = async () => {
      const id = ++requestIdRef.current
      setWorkerRunning(true)
      setSceneReady(false)
      setGeometryFailed(false)
      if (activeWorkerRef.current) {
        activeWorkerRef.current.terminate()
        activeWorkerRef.current = null
      }
      clearWatchdog()

      const hash = await computeGeometryHash(config).catch(() => null)
      if (cancelled || id !== requestIdRef.current) return
      if (hash) {
        const cached = await fetchGeometryCache(hash)
        if (cancelled || id !== requestIdRef.current) return
        if (cached) {
          applyGeometry(cached)
          return
        }
      }
      if (cancelled || id !== requestIdRef.current) return
      // Miss (or hashing failed) — build with the worker and store on success.
      postToWorker(config, hash ?? undefined)
    }
    void buildViaCache()

    return () => {
      cancelled = true
    }
  }, [config, postToWorker, applyGeometry])

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (activeWorkerRef.current) {
        activeWorkerRef.current.terminate()
      }
      clearWatchdog()
    },
    [],
  )

  const initialLoading = result === null && workerRunning
  const updating = result !== null && workerRunning

  return {geometries: result, initialLoading, updating}
}
